import React, { createContext, useContext, useState, useEffect } from 'react';
import { CountdownStyles } from '../components/CountdownTimerStyles';
import { getConfig } from '../config/environment';
import { hasAuthorizedContentAdminToken } from '../utils/adminToken';

const CountdownStyleContext = createContext();

const isAdminSession = () => hasAuthorizedContentAdminToken(getConfig().tlfId);
const siteConfigCountdownStyle = () =>
  window?.SITE_CONFIG?.defaults?.countdown_style || CountdownStyles.GRID;

export function CountdownStyleProvider({ children }) {
  const [countdownStyle, setCountdownStyle] = useState(() => {
    if (isAdminSession()) {
      return localStorage.getItem('countdownStyle') || siteConfigCountdownStyle();
    }
    return siteConfigCountdownStyle();
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleStyleChange = (event) => {
      const newStyle = event.detail;
      updateCountdownStyle(newStyle);
    };

    window.addEventListener('countdownStyleChange', handleStyleChange);
    return () => window.removeEventListener('countdownStyleChange', handleStyleChange);
  }, []);

  // Listen for publish/discard to reset to server values
  useEffect(() => {
    const handleReset = () => {
      try { localStorage.removeItem('countdownStyle'); } catch {}
      setCountdownStyle(siteConfigCountdownStyle());
    };
    window.addEventListener('appearanceReset', handleReset);
    return () => window.removeEventListener('appearanceReset', handleReset);
  }, []);

  // Sync from DraftContext appearance changes (Draft is a child provider; use event bus).
  useEffect(() => {
    const handleDraftSync = (event) => {
      if (!isAdminSession()) return;
      const next = event?.detail?.appearance?.countdownStyle;
      if (typeof next === 'string' && next && next !== countdownStyle) {
        try { localStorage.setItem('countdownStyle', next); } catch {}
        setCountdownStyle(next);
      }
    };
    window.addEventListener('appearanceDraftSync', handleDraftSync);
    return () => window.removeEventListener('appearanceDraftSync', handleDraftSync);
  }, [countdownStyle]);

  const updateCountdownStyle = (newStyle) => {
    setCountdownStyle(newStyle);
    if (isAdminSession()) {
      localStorage.setItem('countdownStyle', newStyle);
    }
  };

  return (
    <CountdownStyleContext.Provider
      value={{
        countdownStyle,
        updateCountdownStyle,
        loading,
      }}
    >
      {children}
    </CountdownStyleContext.Provider>
  );
}

export function useCountdownStyle() {
  const context = useContext(CountdownStyleContext);
  if (!context) {
    throw new Error('useCountdownStyle must be used within a CountdownStyleProvider');
  }
  return context;
}
